import {
  deactivateDiscountCode,
  insertDiscountCode,
  listDiscountCodes,
} from '@repo/db/queries/codes'
import type { DiscountCode } from '@repo/db/schema/discounts'

import { assertPermission, type CoreContext } from '../context.ts'
import type { CoreDeps } from '../deps.ts'
import { type CoreError, dbErr } from '../errors.ts'
import { err, isErr, ok, type Result } from '../result.ts'
import { CreateDiscountCodeInput } from './schemas.ts'

/**
 * Minting codes by hand (spec §14: "Tworzy ręcznie Admin" · H1).
 *
 * Three use-cases and no fourth, deliberately. There is no EDIT: a code is a
 * promise made to whoever holds it, and changing what `WIOSNA24` is worth after
 * somebody has it written down is not an edit, it is a different code. And
 * there is no DELETE: `discount_redemption` references a spent code with
 * `restrict`, because a code somebody used is part of an order's money trail.
 * Turning one off is what removing means here.
 *
 * `offer:manage` rather than `order:manage`: this is the price list, not a
 * customer's order, and it sits with the catalogue in the panel for the same
 * reason.
 */
export function createCodeService(deps: CoreDeps) {
  return {
    /** Every code, newest first — the Admin's list. */
    async listCodes(ctx: CoreContext, limit = 200): Promise<Result<DiscountCode[], CoreError>> {
      const perm = assertPermission(ctx, 'offer', 'manage')
      if (isErr(perm)) return perm

      const rows = await listDiscountCodes(deps.db, limit)
      if (isErr(rows)) return err(dbErr(rows.error.message, rows.error.cause))
      return ok(rows.value)
    },

    /**
     * Mint one.
     *
     * `isConsultationBonus` is deliberately NOT settable here. The one code
     * that stacks with other discounts is the one spec §8 grants for a paid
     * consultation, and it is minted by that flow (I8) rather than typed into a
     * form — a hand-made code carrying the flag would be a way to hand out an
     * exception nobody earned.
     */
    async createCode(ctx: CoreContext, input: unknown): Promise<Result<DiscountCode, CoreError>> {
      const perm = assertPermission(ctx, 'offer', 'manage')
      if (isErr(perm)) return perm

      const parsed = CreateDiscountCodeInput.safeParse(input)
      if (!parsed.success) return err({ kind: 'validation', issues: parsed.error.issues })

      const written = await insertDiscountCode(deps.db, {
        code: parsed.data.code,
        kind: parsed.data.kind,
        value: parsed.data.value,
        validFrom: parsed.data.validFrom ?? null,
        validUntil: parsed.data.validUntil ?? null,
        maxUses: parsed.data.maxUses ?? null,
        minBasketNetMinor: parsed.data.minBasketNetMinor ?? null,
        offeringId: parsed.data.offeringId ?? null,
        createdByUserId: ctx.userId,
      })
      if (isErr(written)) {
        return err(
          written.error.conflict
            ? { kind: 'conflict', message: written.error.message }
            : dbErr(written.error.message, written.error.cause),
        )
      }

      deps.logger?.warn('discount_code.created', {
        code: written.value.code,
        kind: written.value.kind,
      })
      return ok(written.value)
    },

    /** Turn one off. Codes already spent keep their redemptions and their trail. */
    async deactivateCode(ctx: CoreContext, id: string): Promise<Result<DiscountCode, CoreError>> {
      const perm = assertPermission(ctx, 'offer', 'manage')
      if (isErr(perm)) return perm

      const off = await deactivateDiscountCode(deps.db, id, deps.clock.now())
      if (isErr(off)) return err(dbErr(off.error.message, off.error.cause))
      if (!off.value) return err({ kind: 'not_found', entity: 'discount_code', id })

      deps.logger?.warn('discount_code.deactivated', { code: off.value.code })
      return ok(off.value)
    },
  }
}

export type CodeService = ReturnType<typeof createCodeService>
